import React, { useState, useCallback } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import styles from "../../appointmentsPage.module.css";
import { TRiskLevel, IRiskFilters, IOverbookingSuggestion } from "../../types/noShowPredictor.types";
import {
  mockNoShowStats,
  mockRiskCounts,
  mockOverbookingSuggestions,
  mockDayCancellationRates,
  mockPatientsAtRisk
} from "../../mocks/noShowPredictor.mock";
import NoShowStats from "./NoShowStats";
import AutoReminderToggle from "./AutoReminderToggle";
import OverbookingSuggestions from "./OverbookingSuggestions";
import CancellationsChart from "./CancellationsChart";
import RiskFilterPills from "./RiskFilterPills";
import PatientRiskCard from "./PatientRiskCard";

type ToastType = 'success' | 'error' | 'info' | 'warning';

/**
 * No-Show Predictor Component
 * 
 * AI-powered prediction system for appointment cancellations.
 * Shows risk scores, overbooking suggestions, and patient risk cards.
 */
const NoShowPredictor: React.FC = () => {
  // State
  const [autoRemindersEnabled, setAutoRemindersEnabled] = useState(true);
  const [riskFilters, setRiskFilters] = useState<IRiskFilters>({
    high: true,
    medium: true,
    low: true
  });
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // Show toast notification
  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Handlers
  const handleToggleReminders = (enabled: boolean) => {
    setAutoRemindersEnabled(enabled);
    showToast(
      enabled ? t("NOSHOW_TOAST_REMINDERS_ON") : t("NOSHOW_TOAST_REMINDERS_OFF"),
      enabled ? 'success' : 'info'
    );
  };

  const handleFilterChange = (level: TRiskLevel) => {
    setRiskFilters(prev => ({
      ...prev,
      [level]: !prev[level]
    }));
  };

  const handleApplyOverbooking = (suggestion: IOverbookingSuggestion) => {
    const timeSlot = `${suggestion.dayName} ${suggestion.date.split('-').slice(1).reverse().join('/')} • ${suggestion.timeSlot}`;
    if (window.confirm(t("NOSHOW_CONFIRM_OVERBOOKING").replace('{{timeSlot}}', timeSlot))) {
      showToast(t("NOSHOW_TOAST_OVERBOOKING_ADDED"), 'success');
    }
  };

  const handleSendReminder = (patientId: number) => {
    showToast(`📧 ${t("NOSHOW_TOAST_REMINDER_SENT")}`, 'success');
  };

  const handleCall = (patientId: number) => {
    showToast(`📞 ${t("NOSHOW_TOAST_CALLING")}`, 'info');
  };

  const handleAddToWaitlist = (patientId: number) => {
    if (window.confirm(t("NOSHOW_CONFIRM_WAITLIST"))) {
      showToast(`✓ ${t("NOSHOW_TOAST_WAITLIST_ADDED")}`, 'success');
    }
  };

  const handleSendAllReminders = () => {
    const highRiskCount = mockRiskCounts.high;
    if (window.confirm(t("NOSHOW_CONFIRM_SEND_ALL").replace('{{count}}', String(highRiskCount)))) {
      showToast(`📧 ${highRiskCount} ${t("NOSHOW_TOAST_ALL_REMINDERS_SENT")}`, 'success');
    }
  };

  const handleExportData = () => {
    showToast(`📊 ${t("NOSHOW_TOAST_EXPORTING")}`, 'info');
  };

  // Filter patients based on active risk filters
  const filteredPatients = mockPatientsAtRisk.filter(patient => 
    riskFilters[patient.riskLevel]
  );

  return (
    <div className={styles.noShowPredictorContainer}>
      {/* Page Header */}
      <div className={styles.noShowHeader}>
        <h1 className={styles.noShowPageTitle}>
          🔮 {t("NOSHOW_PAGE_TITLE")}
          <span className={styles.aiBadge}>
            <span>🤖</span>
            <span>{t("NOSHOW_AI_BADGE")}</span>
          </span>
        </h1>
        <p className={styles.noShowPageSubtitle}>
          {t("NOSHOW_PAGE_SUBTITLE")}
        </p>
      </div>

      {/* Statistics */}
      <NoShowStats stats={mockNoShowStats} />

      {/* Auto Reminder Toggle */}
      <AutoReminderToggle 
        isEnabled={autoRemindersEnabled} 
        onToggle={handleToggleReminders} 
      />

      {/* Overbooking Suggestions */}
      <OverbookingSuggestions 
        suggestions={mockOverbookingSuggestions}
        onApply={handleApplyOverbooking}
      />

      {/* Cancellations Chart */}
      <CancellationsChart data={mockDayCancellationRates} />

      {/* At-Risk Patients Section */}
      <div className={styles.riskSection}>
        <div className={styles.riskSectionHeader}>
          <h2 className={styles.riskSectionTitle}>
            <span>🎯</span>
            <span>{t("NOSHOW_PATIENTS_TITLE")}</span>
          </h2>
          <div className={styles.riskSectionActions}>
            <button 
              className={`${styles.actionBtn} ${styles.primary}`}
              onClick={handleSendAllReminders}
            >
              📧 {t("NOSHOW_SEND_ALL_REMINDERS")}
            </button>
            <button 
              className={`${styles.actionBtn} ${styles.secondary}`}
              onClick={handleExportData}
            >
              📊 {t("NOSHOW_EXPORT_DATA")}
            </button>
          </div>
        </div>

        {/* Risk Filter Pills */}
        <RiskFilterPills 
          counts={mockRiskCounts}
          filters={riskFilters}
          onFilterChange={handleFilterChange}
        />

        {/* Patient Cards */}
        <div className={styles.patientCards}>
          {filteredPatients.map(patient => (
            <PatientRiskCard
              key={patient.id}
              patient={patient}
              onSendReminder={handleSendReminder}
              onCall={handleCall}
              onAddToWaitlist={handleAddToWaitlist}
            />
          ))}
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`${styles.noShowToast} ${styles[toast.type]}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default NoShowPredictor;
